import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

interface SafeAreaTopProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  /** Extra padding added on top of the safe-area inset (default 0). */
  extra?: number | string
}

/**
 * Adds `padding-top: env(safe-area-inset-top)` so content clears the iOS
 * notch / Dynamic Island. Use wherever a top-anchored element (banner,
 * header, drawer) needs to sit below the notch.
 *
 * Companion to {@link SafeAreaBottom}. Same rule applies: never hand-roll
 * `paddingTop: env(safe-area-inset-top)` elsewhere — Chrome DevTools does
 * not simulate insets and this regression has recurred (FOR-1544, FOR-1584,
 * FOR-1597, FND-ui-1, FND-M-023). Compose this instead.
 */
export function SafeAreaTop({ children, extra = 0, style, ...props }: SafeAreaTopProps) {
  const extraStr = typeof extra === 'number' ? `${extra}px` : extra
  const paddingTop: CSSProperties['paddingTop'] =
    extra === 0
      ? 'env(safe-area-inset-top, 0px)'
      : `calc(env(safe-area-inset-top, 0px) + ${extraStr})`

  return (
    <div style={{ paddingTop, ...style }} {...props}>
      {children}
    </div>
  )
}
